import {isFeatureEnabled} from 'feature-flags';
import {openDialog, TableRowAction} from 'platform/components';

import {
  TemplateResponseBody,
  useHideTemplateMutation,
  useRemoveTemplateMutation,
  useSetPrimaryTemplateV3Mutation,
  useShowTemplateMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {getDocumentTemplateEditorPath} from '../../DocumentTemplateEditor/utils/getDocumentTemplateEditorPath';
import {EditTemplateForm} from '../../TemplateManagement/components/EditTemplateForm';
import {useDownloadTemplate} from './useDownloadTemplate';

export const useTemplateActions = (
  template: TemplateResponseBody & {isPrimary: boolean; definitionType: 'docx' | 'html'}
): TableRowAction[] => {
  const isPrimary = template.isPrimary;
  const isSystem = template.type === 'system';
  const isHidden = template.isHidden;

  const [downloadTemplate] = useDownloadTemplate();
  const navigate = useNavigate();

  const [deleteTemplate] = useRemoveTemplateMutation();
  const [setTemplateAsPrimary] = useSetPrimaryTemplateV3Mutation();
  const [hideTemplate] = useHideTemplateMutation();
  const [showTemplate] = useShowTemplateMutation();

  const handleSetPrimary = () =>
    setTemplateAsPrimary({
      templateId: template.id,
    })
      .unwrap()
      .catch(handleApiError);

  const handleDelete = () =>
    deleteTemplate({templateId: template.id}).unwrap().catch(handleApiError);

  const handleHide = () => hideTemplate({templateId: template.id}).unwrap().catch(handleApiError);

  const handleShow = () => showTemplate({templateId: template.id}).unwrap().catch(handleApiError);

  const handleEdit = () =>
    openDialog(
      <EditTemplateForm
        authorizationProfileId={null}
        documentCode={template.documentCode}
        documentType={template.definitionType}
        template={template}
      />,
      {
        title: i18n.t('entity.documents.labels.uploadEditTemplateDialogTitle'),
      }
    );

  const handleOpenEditor = () => navigate(getDocumentTemplateEditorPath(template.id));

  return buildArray<TableRowAction>()
    .add({
      icon: 'file/download',
      title: `${i18n.t('entity.document.labels.downloadAs')} ${template.definitionType}`,
      onClick: () => downloadTemplate(template),
    })
    .when(!isPrimary && !isHidden, {
      icon: 'action/check_circle',
      title: i18n.t('entity.document.actions.setPrimary'),
      severity: 'success',
      onClick: handleSetPrimary,
    })
    .when(template.canBeHidden && !isHidden, {
      icon: 'action/visibility_off',
      title: i18n.t('general.actions.hide'),
      onClick: handleHide,
    })
    .when(template.canBeHidden && isHidden, {
      icon: 'action/visibility',
      title: i18n.t('general.actions.unhide'),
      onClick: handleShow,
    })
    .when(
      isFeatureEnabled(featureFlags.DOCUMENT_TEMPLATE_EDITOR) && template.definitionType === 'html',
      {
        icon: 'action/open_in_new',
        // System templates open in read-only mode (the backend cannot save edits
        // to them); custom templates are fully editable.
        title: isSystem
          ? i18n.t('entity.documentTemplateEditor.actions.viewInEditor')
          : i18n.t('entity.documentTemplateEditor.actions.openEditor'),
        onClick: handleOpenEditor,
      }
    )
    .whenNot(isSystem, {
      icon: 'image/edit',
      title: i18n.t('general.actions.edit'),
      onClick: handleEdit,
    })
    .when(template.canBeDeleted, {
      icon: 'action/delete',
      title: i18n.t('general.actions.delete'),
      severity: 'danger',
      onClick: handleDelete,
    });
};
